import React, { useState, useEffect } from 'react';
import { FormLabel, Typography } from '@mui/material';
import Upload from '@mui/icons-material/CloudUpload';
import { FormattedMessage, useIntl } from 'react-intl';
import { get, has } from 'lodash';

import withOptional from '../../../../../Utils/Posts/Form/withOptional';
import EmptyState from '../../../../DesignSystem/EmptyState';
import PreviewFiles from './PreviewFiles';
import PreviewImages from './PreviewImages';

function Input({
  name,
  value: attachments,
  onChange,
  errors,
  setError,
  setDisabledForm,
  required = false,
  noTitle = false,
  inputProps = {},
}: any) {
  const intl = useIntl();
  // We need to store the attachments in a temporary state via `values` because `onChange`
  // does not provide the updated value which is causing race conditions when we upload several files.
  const [values, setValues] = useState(attachments);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (attachments.some((attachment: any) => attachment.virus_scan_status === 'infected')) {
      setError(name, {
        message: intl.formatMessage({ id: 'VIRUS_SCAN.INFECTED_ATTACHMENTS_ERROR' }),
      });
    }
  }, [attachments]);

  useEffect(() => {
    // To update `values` with the attachments returned from the API after a save
    if (
      (!initialized && values.length !== attachments.length) ||
      values.some((item: any, index: number) => item.id !== attachments[index]?.id)
    ) {
      setInitialized(true);
      setValues(attachments);
    }
  }, [attachments]);

  useEffect(() => {
    // We need to call the `onChange` prop to update the form value.
    // A cleaner way would be to call it inside `setValues` in the `onUpload` function
    // but it's not possible to call a hook in a hook
    onChange(values);
  }, [values]);

  const onUpload = (blob: any) => {
    // we need the `currentValues` to avoid race conditions
    setValues((currentValues: any) => {
      const newValues = currentValues.map((currentValue: any) =>
        currentValue instanceof File && currentValue.name === blob.title ? blob : currentValue,
      );

      return newValues;
    });
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;

    if (droppedFiles.length > 0) {
      setUniqueValues(droppedFiles);
    }
  };

  const setUniqueValues = (newFiles: any) => {
    setValues((currentValues: any) => {
      const uniqueNewFiles = Array.from(newFiles).filter(
        (file: any) => !currentValues.some((value: any) => value.title === file.name),
      );

      return [...currentValues, ...uniqueNewFiles];
    });
  };

  const handleDelete = (file: any) => {
    const updatedValue = values.map((value: any) => {
      if (value.blob_id === file.blob_id) {
        return { ...value, _destroy: true };
      }

      return value;
    });

    setValues(updatedValue);
    onChange(updatedValue);
  };

  // hide files that have the prop `_destroy: true`
  const filesToShow = values.filter((file: any) => !file._destroy);
  const imagesFiles = filesToShow.filter(({ type }: any) => type.includes('image'));
  const otherFiles = filesToShow.filter((file: any) => !imagesFiles.includes(file));

  return (
    <>
      {!noTitle && (
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          {withOptional(intl.formatMessage({ id: 'POST_FORM.ATTACHMENTS.TITLE' }), required)}
        </Typography>
      )}

      {has(errors, name) && (
        <Typography color="error" sx={{ mb: 1 }}>
          {get(errors, name)?.message}
        </Typography>
      )}

      <EmptyState
        component={FormLabel}
        htmlFor={`${name}-attachment-button`}
        onDrop={handleDrop}
        onDragOver={(event: any) => event.preventDefault()}
        icon={<Upload />}
        content={
          <>
            {inputProps.title || <FormattedMessage id="BUTTONS.DRAG_DROP_ATTACHMENTS" />}
            <input
              type="file"
              name="files"
              onChange={(e: any) => {
                setUniqueValues(e.target.files);
              }}
              multiple
              style={{ display: 'none' }}
              id={`${name}-attachment-button`}
              {...inputProps}
            />
          </>
        }
      />

      <PreviewFiles
        files={otherFiles}
        name={name}
        setDisabledForm={setDisabledForm}
        onUpload={onUpload}
        handleDelete={handleDelete}
        required={required}
      />

      <PreviewImages
        files={imagesFiles}
        name={name}
        setDisabledForm={setDisabledForm}
        onUpload={onUpload}
        handleDelete={handleDelete}
        required={required}
      />
    </>
  );
}

export default Input;
